import * as React from "react";
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import TranslateNew from '@translate/TranslateNew';
import {IArticleTarget} from '@app-types/article.types';
import {openArticle} from '@services/article/article.actions';
import {articleTargetForLink} from '@containers/knowledge/articleNavigation';
import {showAlreadyLoggedInNotice} from '@services/accountLink/accountLink.actions';
import {hasAuthToken} from '@services/user/user.utils';
import {isAccountLinkPath, isNewAccountLink} from './accountLinks';
import {SETTINGS_PATH} from './safeReturn';
import {runCordovaPluginCall} from '@helpers/cordovaBridge';

const tn: TranslateNew = TranslateNew.getInstance();
declare const window: any;

// The Meta SDK registers the fb<app id>:// scheme and receives those URLs itself (Facebook
// Login returns, Meta App Links). AppsFlyer reports them too, but they are not app routes.
const META_SDK_SCHEME = /^fb\d+[a-z0-9]*:/i;

interface IProps extends RouteComponentProps {
  id:number|undefined;
  loggedIn: boolean;
  openArticle: (target: IArticleTarget) => void;
  showAlreadyLoggedInNotice: () => void;
}


interface IUrlResult {
  url: string;
  path: string;
  queryString: string;
  scheme: string;
  host: string;
  fragment: string;
}

interface IReturnParams {
  route: any,
  args: any,
  link: any,
}



export class Deeplinking extends React.Component<IProps> {

  public render() {
    return (
      <div/>
    );
  }

  public componentDidMount() {
    document.addEventListener("deviceready", () => this.onDeviceReady(this.props));
  }

  public onDeviceReady(props:IProps) {
    if (!window.plugins || !window.plugins.appsFlyer) {
      console.error("AppsFlyer plugin not available");
      return;
    }
    
    runCordovaPluginCall("AppsFlyer.registerDeepLink", () => window.plugins.appsFlyer.registerDeepLink((data:any) => {
     try {
      if (data !== undefined && typeof data === 'string' && data.includes("vvecka")) {
        // alert(data);
        return; // this is an object shared from the ext and should not be handled here
      }

      const link = this.extractLink(data);
      if (link === null || META_SDK_SCHEME.test(link)) {
        return;
      }
      this.handleLink(link);
     } catch (err) {
       (window as any).reportHandledError?.('AppsFlyer deep link handler', err, {
         dataType: typeof data,
       });
     }

    }, (error:any) => {
      console.log("appsFlyer deep link error: ");
    }));

    if (window.cordova?.platformId === 'ios' && typeof window.plugins.appsFlyer.disableSKAD === 'function') {
      // AppsFlyer is only kept for deep links. Leave SKAdNetwork conversion values to Meta's SDK
      // so two SDKs don't overwrite each other. Must run before initSdk; deep links are unaffected.
      try {
        window.plugins.appsFlyer.disableSKAD(true);
      } catch (err) {
        console.warn("Could not disable AppsFlyer SKAdNetwork handling", err);
      }
    }

    runCordovaPluginCall("AppsFlyer.initSdk", () => window.plugins.appsFlyer.initSdk(
      {
        devKey: 'AbkXk4q7B5XcK9ZgX56nYa', // your AppsFlyer devKey  
        isDebug: false,  
        appId: '1618305857', // your ios appID  
        // waitForATTUserAuthorization: 10, // time for the sdk to wait before launch - IOS 14 ONLY!  
        onInstallConversionDataListener:false,
        onDeepLinkListener: true
      }, (result:string) => {  
        /*
        if (props && props.id) {
          window.plugins.appsFlyer.setAppUserId((""+props.id));
          /*
          const  getUserIdCallbackFn = (id:string) => {
            console.log("appsflyer id: "+id); 
          }
          window.plugins.appsFlyer.getAppsFlyerUID(getUserIdCallbackFn); 
        }
        */
      }, (error:string) => {  console.error("appsflyer error: ");} 
    ));


  }

  private handleLink(link: string) {

    const path = this.splitLink(link);
    if (path === null) {
      return
    }
    const article = articleTargetForLink(path);
    if (article) {
      this.props.openArticle(article);
      return;
    }
    if (isAccountLinkPath(path)) {
      const isNew = isNewAccountLink(path);
      if (this.props.loggedIn) {
        if (isNew) {
          this.props.history.push(SETTINGS_PATH);
          this.props.showAlreadyLoggedInNotice();
        }
        return;
      }
    }
    this.props.history.push(path);
  }

  private extractLink(jsonString: string): string | null {
      try {
          const parsed = JSON.parse(jsonString);
          return parsed?.data?.link || null;
      } catch (error) {
          console.error("Invalid AppsFlyer deep-link payload");
          return null;
      }
  }

  private splitLink(link:string): string | null {
    const data = this.createResult(link);
    // alert(data);
    const realPath = this.getRealPath(data);
    // alert(realPath);
    if (realPath == null) {
      return null;
    };
    return realPath;
  } 


  private getRealPath(data: IUrlResult): string | null {
    // 1. Let's just do the obvious and return the parsed 'path' first, if available.
    if (!!data.path && data.path !== "") {
      return this.appendQueryString(data.path, data.queryString);
    }

    // 2. Now, are we using a non-standard scheme?
    const isCustomScheme = data.scheme.indexOf('http') === -1;

    // 3. Nope so we'll go fragment first if available as that should be what comes after
    if (!isCustomScheme) {
      if (!!data.fragment) {
        return this.stripFragmentLeadingHash(data.fragment);
      }
    }

    // 4. Now fall back to host / authority
    if (!!data.host) {
      if (data.host.charAt(0) !== '/') {
        data.host = '/' + data.host;
      }

      return this.appendQueryString(data.host, data.queryString);
    }

    // 5. We'll use fragment next if we're in a custom scheme, though this might need a little more thought
    if (isCustomScheme && !!data.fragment) {
      return this.stripFragmentLeadingHash(data.fragment);
    }

    // 6. Last resort - no obvious path, fragment or host, so we
    // slice out the scheme and any query string or fragment from the full url.
    let restOfUrl = data.url;
    let separator = data.url.indexOf('://');

    if (separator !== -1) {
      restOfUrl = data.url.slice(separator + 3);
    } else {
      separator = data.url.indexOf(':/');
      if (separator !== -1) {
        restOfUrl = data.url.slice(separator + 2);
      }
    }

    const qs = restOfUrl.indexOf('?');
    if (qs > -1) {
      restOfUrl = restOfUrl.slice(0, qs);
    }

    const hs = restOfUrl.indexOf('#');
    if (hs > -1) {
      restOfUrl = restOfUrl.slice(0, hs);
    }

    return this.appendQueryString(restOfUrl, data.queryString);
  }
  
  private createResult(urlString: string): IUrlResult {
    try {
      const url = new URL(urlString);
      return {
        url: url.href || '',
        path: url.pathname || '',
        // Remove the leading '?' if present.
        queryString: url.search ? url.search.substring(1) : '',
        // Remove the trailing ':' from the protocol (e.g., "https:")
        scheme: url.protocol ? url.protocol.replace(':', '') : '',
        host: url.host || '',
        // Remove the leading '#' if present.
        fragment: url.hash ? url.hash.substring(1) : '',
      };
    } catch (error) {
      console.error("Invalid deep-link URL");
      return {
        url: '',
        path: '',
        queryString: '',
        scheme: '',
        host: '',
        fragment: '',
      };
    }
  }


  private stripFragmentLeadingHash(fragment: any) {
    const hs = fragment.indexOf('#');

    if (hs > -1) {
      fragment.slice(0, hs);
    }

    return fragment;
  }

  private appendQueryString(path:string, queryString:string):string {
    if (!queryString) {
      return path;
    }

    return path.indexOf("?") === -1
      ? path+"?"+queryString
      : path+"&"+queryString;
  }
}

export function mapStateToProps(state: any) {
  const loggedIn = hasAuthToken(state);

  if (state.user.session && state.user.session.data && state.user.session.data.id) {
    return {
      id:0,
      loggedIn,
    };
  }
  return {
    id:undefined,
    loggedIn,
  };
}


const PublicRouterSnackbar = compose(
  withRouter,
  connect(mapStateToProps, {openArticle, showAlreadyLoggedInNotice})
)(Deeplinking);

export default PublicRouterSnackbar;



// WEBPACK FOOTER //
// ./src/router/Deeplinking.tsx